import { Group, Vector3, Color } from 'three';
import { settings } from '../config/settings.js';
import { saturate, Easing } from '../utils/math.js';
import { getColor } from '../utils/color.js';
import { LAYER } from '../core/Layers.js';

export const AbilityPhase = Object.freeze({
  IDLE: 'idle',
  TRAVEL: 'travel',
  IMPACT: 'impact',
  FADE: 'fade',
  DONE: 'done'
});

const _up = new Vector3(0, 1, 0);

/**
 * Abstract base for a linear skillshot.
 *
 * The sandbox used to cast along a freehand spline; it now casts along a **line**
 * chosen with the aim indicator, so this base is correspondingly small. What it
 * still owns, because every ability wants it and none of them should re-derive
 * it:
 *
 *   - the phase machine (travel → impact → fade → done)
 *   - a front that advances along the line at a constant metres-per-second,
 *     frame-rate independent and eased off a standstill
 *   - the local frame (`direction`, `side`) every effect places itself in
 *   - dynamic light bookkeeping, including a decaying impact punch
 *   - the pooling contract: `spawn` must fully reset state, `destroy` must
 *     release, and neither may allocate
 *
 * Subclasses implement: `createShaders`, `createParticles`, `onSpawn`,
 * `onTravel`, `onImpact`, `onFade`, `onDestroy`.
 *
 * A second ability is therefore one new file plus a settings block — nothing
 * else in the project changes.
 */
export class Ability {
  /**
   * @param {string} element  key into `settings` ('flux')
   * @param {object} context  shared systems (see AbilityManager)
   */
  constructor(element, context) {
    this.element = element;
    this.ctx = context;

    this.group = new Group();
    this.group.name = `Ability:${element}`;
    this.group.layers.set(LAYER.VFX);
    this.group.matrixAutoUpdate = false;

    this.phase = AbilityPhase.IDLE;

    /** Where the cast came from, on the floor. */
    this.origin = new Vector3();
    /** Unit heading, flat. */
    this.direction = new Vector3(0, 0, 1);
    /** Unit lateral, `direction × up`. */
    this.side = new Vector3(1, 0, 0);
    /** How far the cast reaches, metres. */
    this.length = 1;

    /** Metres the fracture front has travelled. */
    this.front = 0;
    /** That front as a fraction of `length`. */
    this.u = 0;
    /** World position of the front — what the camera frames. */
    this.position = new Vector3();

    this.age = 0;
    this.impactTime = 0;
    this.fadeTime = 0;

    this.light = null;
    this.lightColor = new Color();
    /** Transient additive light punch (impacts). Decays on its own. */
    this.lightBoost = 0;

    this.createShaders();
    this.createParticles();
  }

  /** Live settings block for this element. */
  get config() {
    return settings[this.element];
  }

  get isActive() {
    return this.phase !== AbilityPhase.IDLE && this.phase !== AbilityPhase.DONE;
  }

  get isFinished() {
    return this.phase === AbilityPhase.DONE;
  }

  /** Instanced geometry this cast is currently drawing. HUD readout only. */
  get instanceCount() {
    return 0;
  }

  /**
   * How hard the camera rig should drift toward `position`, 0..1.
   *
   * A cast is followed less the further its front has got — the impact is
   * what the shot was for, and the view should already be there when it
   * lands. A construct that roams (the drone) overrides this to stay framed.
   */
  get cameraWeight() {
    return saturate(1 - this.u * 0.4);
  }

  /**
   * Whether this ability decides for itself what it hits.
   *
   * `DummyField` reads the line every cast publishes and turns it into a kill
   * volume, which is right for everything that simply *reaches*. It is wrong
   * for a cast that picks its targets — the Chrono-Summon fires on one body at
   * a time and has to be the thing that says which, and when. Those abilities
   * answer true here and the field leaves them alone.
   */
  get handlesOwnHits() {
    return false;
  }

  /* ------------------------------------------------------------------ */
  /* Subclass hooks                                                      */
  /* ------------------------------------------------------------------ */

  /** Build materials/meshes once, at construction. */
  createShaders() {}

  /** Register the shared particle systems this element needs. */
  createParticles() {}

  /** Called at the start of every cast, after the base state was reset. */
  onSpawn() {}

  /** Per-frame while the front is still travelling. */
  onTravel(_dt) {}

  /** One-shot when the front reaches the end of the line. */
  onImpact() {}

  /**
   * Per-frame after the impact.
   * @param {number} t 0..1 through the impact phase, then 1..2 through the fade.
   */
  onFade(_dt, _t) {}

  /** Release any per-cast resources. */
  onDestroy() {}

  /** How long the impact and fade phases last. Overridable per element. */
  get impactDuration() {
    return 1.1;
  }

  get fadeDuration() {
    return 1.2;
  }

  /**
   * Per-frame multiplier on the dynamic light's intensity.
   *
   * The default is a slow shimmer rather than a flicker: ice glints, it does not
   * gutter. Elements that *should* gutter override this.
   */
  lightShimmer() {
    return 0.9 + 0.1 * Math.sin(this.age * 9.3) * Math.sin(this.age * 3.7);
  }

  /* ------------------------------------------------------------------ */
  /* Lifecycle                                                           */
  /* ------------------------------------------------------------------ */

  /**
   * Begin a cast.
   *
   * @param {THREE.Vector3} origin     on the floor
   * @param {THREE.Vector3} direction  unit, flat
   * @param {number} distance          metres
   */
  spawn(origin, direction, distance) {
    this.origin.set(origin.x, 0, origin.z);
    this.direction.copy(direction).setY(0).normalize();
    this.side.crossVectors(this.direction, _up).normalize();
    this.length = Math.max(0.1, distance);

    this.front = 0;
    this.u = 0;
    this.age = 0;
    this.impactTime = 0;
    this.fadeTime = 0;
    this.lightBoost = 0;
    this.phase = AbilityPhase.TRAVEL;

    this.position.copy(this.origin);
    this.light = this.ctx.lights.acquire();

    this.group.visible = true;
    this.onSpawn();
  }

  /** A point on the cast line. `s` is 0..1 along it. */
  pointAt(s, out) {
    return out.copy(this.origin).addScaledVector(this.direction, s * this.length);
  }

  /**
   * Advance the front. Delta-time driven, so the eruption travels at a constant
   * metres-per-second regardless of frame rate or how long the cast is.
   *
   * @returns {boolean} true on the frame the front reaches the end
   */
  advance(dt) {
    const speed = this.config.speed * settings.global.speed;
    // Ease off the standstill so the front has weight. Keyed off elapsed time
    // rather than progress: keying it off `u` would multiply the very first step
    // by zero and the front could never leave the caster.
    const easeIn = Easing.outQuad(saturate(this.age / 0.08));

    this.front += speed * easeIn * dt;
    const previousU = this.u;
    this.u = saturate(this.front / this.length);
    this.pointAt(this.u, this.position);

    return this.u >= 1 && previousU < 1;
  }

  update(dt) {
    if (!this.isActive) return;
    this.age += dt;

    switch (this.phase) {
      case AbilityPhase.TRAVEL: {
        const reachedEnd = this.advance(dt);
        this.onTravel(dt);
        this._updateLight(dt, 1);
        if (reachedEnd) {
          this.phase = AbilityPhase.IMPACT;
          this.impactTime = 0;
          this.onImpact();
        }
        break;
      }

      case AbilityPhase.IMPACT: {
        this.impactTime += dt;
        const t = saturate(this.impactTime / this.impactDuration);
        this.onFade(dt, t);
        this._updateLight(dt, 1 - Easing.inQuad(t) * 0.45);
        if (t >= 1) {
          this.phase = AbilityPhase.FADE;
          this.fadeTime = 0;
        }
        break;
      }

      case AbilityPhase.FADE: {
        this.fadeTime += dt;
        const t = saturate(this.fadeTime / this.fadeDuration);
        this.onFade(dt, 1 + t);
        this._updateLight(dt, (1 - t) * 0.35);
        if (t >= 1) this.phase = AbilityPhase.DONE;
        break;
      }

      default:
        break;
    }
  }

  _updateLight(dt, scale) {
    if (!this.light) return;
    const cfg = this.config;
    this.lightColor.copy(getColor(cfg.lightColor));
    const shimmer = this.lightShimmer();
    this.ctx.lights.set(
      this.light,
      this.position,
      this.lightColor,
      cfg.lightIntensity * scale * shimmer + this.lightBoost,
      cfg.lightRadius * (1 + this.lightBoost * 0.02),
      dt
    );
    this.lightBoost = Math.max(0, this.lightBoost - this.lightBoost * 4.5 * dt - 0.5 * dt);
  }

  /** Return to the pool. Must leave the instance reusable. */
  destroy() {
    this.onDestroy();
    this.ctx.lights.release(this.light);
    this.light = null;
    this.group.visible = false;
    this.phase = AbilityPhase.IDLE;
  }

  /** Free GPU resources (app teardown only — not part of pooling). */
  dispose() {
    this.group.parent?.remove(this.group);
  }
}
